import {
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Textarea,
  toast,
} from '@berky/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { Send } from 'lucide-react'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'

import {
  useFeedbackAblehnen,
  useFeedbackInBearbeitungSetzen,
  useFeedbackKommentarHinzufuegen,
  useFeedbackUmsetzungHinzufuegen,
  useFeedbackZurueckstellen,
} from '../api'
import {
  feedbackCommentSchema,
  type FeedbackCommentFormValues,
} from '../feedback-activity-schema'
import type { FeedbackStatus } from '../types'

type Bestaetigungsaktion = 'erledigt' | 'ablehnen' | null

export function FeedbackConversationComposer({
  feedbackId,
  modulId,
  status,
  zurueckgestelltBis,
  managementActions = true,
}: {
  feedbackId: string
  modulId: string | null
  status: FeedbackStatus
  zurueckgestelltBis?: string | null
  managementActions?: boolean
}) {
  const [bestaetigungsaktion, setBestaetigungsaktion] =
    useState<Bestaetigungsaktion>(null)
  const [zurueckstellenDialogOpen, setZurueckstellenDialogOpen] = useState(false)
  const [wiedervorlageDatum, setWiedervorlageDatum] = useState('')
  const kommentarMutation = useFeedbackKommentarHinzufuegen(feedbackId)
  const bearbeitungMutation = useFeedbackInBearbeitungSetzen(feedbackId)
  const umsetzungMutation = useFeedbackUmsetzungHinzufuegen(feedbackId)
  const ablehnungMutation = useFeedbackAblehnen(feedbackId)
  const zurueckstellungMutation = useFeedbackZurueckstellen(feedbackId)
  const form = useForm<FeedbackCommentFormValues>({
    resolver: zodResolver(feedbackCommentSchema),
    defaultValues: { text: '' },
  })
  const text = useWatch({ control: form.control, name: 'text' })
  const mutationLaeuft =
    kommentarMutation.isPending ||
    bearbeitungMutation.isPending ||
    umsetzungMutation.isPending ||
    ablehnungMutation.isPending ||
    zurueckstellungMutation.isPending
  const istLeer = !text.trim()

  function zuruecksetzen() {
    form.reset()
    setBestaetigungsaktion(null)
  }

  const nachrichtSenden = form.handleSubmit((values) => {
    kommentarMutation.mutate(values.text, {
      onSuccess: () => {
        zuruecksetzen()
        toast.success('Nachricht gesendet')
      },
      onError: (error) =>
        toast.error('Nachricht konnte nicht gesendet werden', {
          description: error.message,
        }),
    })
  })

  async function bestaetigungOeffnen(aktion: Exclude<Bestaetigungsaktion, null>) {
    if (await form.trigger('text')) setBestaetigungsaktion(aktion)
  }

  async function zurueckstellungOeffnen() {
    if (!(await form.trigger('text'))) return

    setWiedervorlageDatum(
      zurueckgestelltBis ? datumFuerEingabe(new Date(zurueckgestelltBis)) : '',
    )
    setZurueckstellenDialogOpen(true)
  }

  const alsInArbeitSenden = form.handleSubmit((values) => {
    bearbeitungMutation.mutate(values.text, {
      onSuccess: () => {
        zuruecksetzen()
        toast.success('Nachricht gesendet und Bearbeitung gestartet')
      },
      onError: (error) =>
        toast.error('Bearbeitung konnte nicht gestartet werden', {
          description: error.message,
        }),
    })
  })

  function alsErledigtSenden() {
    umsetzungMutation.mutate(
      {
        ziel: modulId ? 'Modul' : 'Plattform',
        modulId,
        branch: null,
        pullRequest: null,
        buildId: null,
        ueberVibeCoding: false,
        notiz: form.getValues('text'),
      },
      {
        onSuccess: () => {
          zuruecksetzen()
          toast.success('Nachricht gesendet und Feedback erledigt')
        },
        onError: (error) => {
          setBestaetigungsaktion(null)
          toast.error('Feedback konnte nicht erledigt werden', {
            description: error.message,
          })
        },
      },
    )
  }

  function alsAblehnungSenden() {
    ablehnungMutation.mutate(form.getValues('text'), {
      onSuccess: () => {
        zuruecksetzen()
        toast.success('Nachricht gesendet und Feedback abgelehnt')
      },
      onError: (error) => {
        setBestaetigungsaktion(null)
        toast.error('Feedback konnte nicht abgelehnt werden', {
          description: error.message,
        })
      },
    })
  }

  function alsZurueckgestelltSenden() {
    if (!wiedervorlageDatum) return

    zurueckstellungMutation.mutate(
      {
        grund: form.getValues('text'),
        wiedervorlageAm: new Date(`${wiedervorlageDatum}T00:00:00`).toISOString(),
      },
      {
        onSuccess: () => {
          zuruecksetzen()
          setZurueckstellenDialogOpen(false)
          toast.success('Feedback zurückgestellt')
        },
        onError: (error) =>
          toast.error('Feedback konnte nicht zurückgestellt werden', {
            description: error.message,
          }),
      },
    )
  }

  return (
    <>
      <form onSubmit={nachrichtSenden} noValidate className="space-y-3 p-4 sm:p-5">
        <FormField
          label={managementActions ? 'Kommentar/Status ändern' : 'Nachricht'}
          htmlFor="feedback-nachricht"
          required
          error={form.formState.errors.text?.message}
        >
          <Textarea
            id="feedback-nachricht"
            rows={3}
            maxLength={2000}
            placeholder="Kommentar schreiben …"
            aria-invalid={Boolean(form.formState.errors.text)}
            aria-describedby={
              form.formState.errors.text ? 'feedback-nachricht-error' : undefined
            }
            {...form.register('text')}
            onKeyDown={(event) => {
              if (
                event.key === 'Enter' &&
                !event.shiftKey &&
                !event.nativeEvent.isComposing
              ) {
                event.preventDefault()
                void nachrichtSenden()
              }
            }}
          />
        </FormField>

        <div
          className="flex flex-wrap items-center gap-1.5"
          role="group"
          aria-label={
            managementActions ? 'Kommentar oder Status ändern' : 'Nachricht senden'
          }
        >
          {managementActions && (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-warning bg-warning-surface px-2 text-warning-strong hover:bg-warning-surface/80 hover:text-warning-strong focus-visible:ring-warning/30 dark:border-warning dark:bg-warning-surface dark:hover:bg-warning-surface/80"
                disabled={istLeer || mutationLaeuft || status === 'In Bearbeitung'}
                onClick={() => void alsInArbeitSenden()}
              >
                In Arbeit
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-border bg-neutral-surface px-2 text-neutral-strong hover:bg-neutral-surface/80 hover:text-neutral-strong"
                disabled={istLeer || mutationLaeuft}
                onClick={() => void zurueckstellungOeffnen()}
              >
                Später
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-success bg-success-surface px-2 text-success-strong hover:bg-success-surface/80 hover:text-success-strong focus-visible:ring-success/30 dark:border-success dark:bg-success-surface dark:hover:bg-success-surface/80"
                disabled={istLeer || mutationLaeuft || status === 'Erledigt'}
                onClick={() => void bestaetigungOeffnen('erledigt')}
              >
                Erledigt
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-destructive bg-destructive-surface px-2 text-destructive-strong hover:bg-destructive-surface/80 hover:text-destructive-strong focus-visible:ring-destructive/30 dark:border-destructive dark:bg-destructive-surface dark:hover:bg-destructive-surface/80"
                disabled={istLeer || mutationLaeuft || status === 'Abgelehnt'}
                onClick={() => void bestaetigungOeffnen('ablehnen')}
              >
                Ablehnen
              </Button>
            </>
          )}
          <Button
            type="submit"
            size="sm"
            className="ml-auto px-2 shadow-sm"
            disabled={istLeer || mutationLaeuft}
          >
            <Send aria-hidden="true" />
            {kommentarMutation.isPending ? 'Wird gesendet …' : 'Nachricht'}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={bestaetigungsaktion === 'erledigt'}
        onOpenChange={(open) => !open && setBestaetigungsaktion(null)}
        title="Feedback als erledigt markieren?"
        description="Die Nachricht wird als Fertigmeldung gesendet und der Status auf „Erledigt“ gesetzt."
        confirmLabel="Nachricht senden und erledigen"
        pending={umsetzungMutation.isPending}
        onConfirm={alsErledigtSenden}
      />

      <ConfirmDialog
        open={bestaetigungsaktion === 'ablehnen'}
        onOpenChange={(open) => !open && setBestaetigungsaktion(null)}
        title="Feedback ablehnen?"
        description="Die Nachricht wird als Begründung gesendet und der Status auf „Abgelehnt“ gesetzt."
        confirmLabel="Nachricht senden und ablehnen"
        destructive
        pending={ablehnungMutation.isPending}
        onConfirm={alsAblehnungSenden}
      />

      <Dialog
        open={zurueckstellenDialogOpen}
        onOpenChange={(open) => {
          if (!zurueckstellungMutation.isPending) setZurueckstellenDialogOpen(open)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Feedback zurückstellen</DialogTitle>
            <DialogDescription>
              Am gewählten Tag wird das Feedback automatisch wieder auf „Neu“ gesetzt.
            </DialogDescription>
          </DialogHeader>

          <FormField label="Wieder vorlegen am" htmlFor="feedback-wiedervorlage" required>
            <Input
              id="feedback-wiedervorlage"
              type="date"
              min={morgenAlsEingabedatum()}
              value={wiedervorlageDatum}
              onChange={(event) => setWiedervorlageDatum(event.target.value)}
            />
          </FormField>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={zurueckstellungMutation.isPending}
              onClick={() => setZurueckstellenDialogOpen(false)}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={!wiedervorlageDatum || zurueckstellungMutation.isPending}
              onClick={alsZurueckgestelltSenden}
            >
              {zurueckstellungMutation.isPending
                ? 'Wird zurückgestellt …'
                : 'Zurückstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function datumFuerEingabe(datum: Date) {
  const jahr = datum.getFullYear()
  const monat = String(datum.getMonth() + 1).padStart(2, '0')
  const tag = String(datum.getDate()).padStart(2, '0')
  return `${jahr}-${monat}-${tag}`
}

function morgenAlsEingabedatum() {
  const morgen = new Date()
  morgen.setDate(morgen.getDate() + 1)
  return datumFuerEingabe(morgen)
}
